/* eslint-disable @typescript-eslint/strict-boolean-expressions, @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useRef, useEffect } from "react"
import { observer } from "mobx-react"
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Alert,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Divider,
    Paper,
} from "@mui/material"

import { ChatbotTestExecutionService } from "src/api/services/ChatbotTestExecutionService"
import type { adminv3_ChatbotTestConfigurationResponse } from "src/api/models/adminv3_ChatbotTestConfigurationResponse"
import type { adminv3_UpdateChatbotTestExecutionRequest } from "src/api/models/adminv3_UpdateChatbotTestExecutionRequest"

import { DEFAULT_ACCESS_GROUP } from "src/config"

import { GlobalStore } from "src/store"
import { useStore } from "src/store/lib/useStore"

interface ITestRunnerProps {
    selectedConfiguration: adminv3_ChatbotTestConfigurationResponse | null
}

type ChatbotMessage = {
    message_type?: string
    text?: string
    content?: string
    message?: string
    options?: Array<{ text?: string }>
    meta_data?: Record<string, unknown>
}

export const TestRunner: React.FC<ITestRunnerProps> = observer(
    ({ selectedConfiguration }) => {
        const globalStore = useStore(GlobalStore)
        const [isRunning, setIsRunning] = useState(false)
        const [testResult, setTestResult] = useState<null | {
            success: boolean
            turnCount: number
            duration: number
            completionReason: string
        }>(null)
        const [error, setError] = useState<string | null>(null)
        const [verboseLogging, setVerboseLogging] = useState(false)
        const [selectedAccessGroupId, setSelectedAccessGroupId] =
            useState<number>(
                globalStore.session.accessGroup?.id || DEFAULT_ACCESS_GROUP.id,
            )
        const [testLog, setTestLog] = useState<string[]>([])
        const testLogRef = useRef<HTMLDivElement>(null)

        // Configuration options - defaults
        const [tenantId, setTenantId] = useState("51002")
        const [subdomain, setSubdomain] = useState("stage-prodteam.avy.se")
        const [apiEnvironment, setApiEnvironment] = useState("stage")

        // Update defaults when configuration changes
        useEffect(() => {
            if (selectedConfiguration?.test_config) {
                const testConfig =
                    selectedConfiguration.test_config as unknown as {
                        tenant_id?: string
                        subdomain?: string
                        api_environment?: string
                    } | null

                if (testConfig) {
                    setTenantId(testConfig.tenant_id || "51002")
                    setSubdomain(
                        testConfig.subdomain || "stage-prodteam.avy.se",
                    )
                    setApiEnvironment(testConfig.api_environment || "stage")
                } else {
                    setTenantId("51002")
                    setSubdomain("stage-prodteam.avy.se")
                    setApiEnvironment("stage")
                }
            } else {
                setTenantId("51002")
                setSubdomain("stage-prodteam.avy.se")
                setApiEnvironment("stage")
            }
        }, [selectedConfiguration])

        const logMessage = useCallback(
            (message: string, isVerbose: boolean = false) => {
                // Skip verbose messages if verbose logging is disabled
                if (isVerbose && !verboseLogging) return

                const timestamp = new Date().toLocaleTimeString()
                const logEntry = `[${timestamp}] ${message}`
                setTestLog((prev) => [...prev, logEntry])

                // Auto-scroll to bottom
                setTimeout(() => {
                    if (testLogRef.current != null) {
                        testLogRef.current.scrollTop =
                            testLogRef.current.scrollHeight
                    }
                }, 100)
            },
            [verboseLogging],
        )

        // Helper function to check end conditions
        const checkEndCondition = useCallback(
            (
                endCondition: {
                    condition?: {
                        message_contains?: string
                        message_type?: string
                    }
                    result?: "pass" | "fail"
                    description?: string
                },
                message: ChatbotMessage,
            ) => {
                let conditionMet = false
                if (
                    (endCondition.condition?.message_contains ?? "").length > 0
                ) {
                    // Check multiple possible text fields in the message
                    const textToCheck =
                        message.text || message.content || message.message || ""
                    conditionMet = textToCheck.includes(
                        endCondition.condition?.message_contains ?? "",
                    )
                } else if (
                    (endCondition.condition?.message_type ?? "").length > 0
                ) {
                    conditionMet =
                        (message.message_type ?? "") ===
                        (endCondition.condition?.message_type ?? "")
                }
                return {
                    met: conditionMet,
                    result: endCondition.result ?? "fail", // pass or fail
                    description:
                        endCondition.description ?? "End condition met",
                }
            },
            [],
        )

        // Helper function to process rule responses
        const processRuleResponse = useCallback(
            (response: string, currentMessage: ChatbotMessage) => {
                if (response === "skip") {
                    return "skip"
                }
                if (response === "first_option_text") {
                    return currentMessage.options?.[0]?.text || null
                }
                if (response === "second_option_text") {
                    return currentMessage.options?.[1]?.text || null
                }
                if (response === "third_option_text") {
                    return currentMessage.options?.[2]?.text || null
                }
                if (response === "confirm_contact_details") {
                    // Confirm contact details as-is - send back the original meta_data
                    return JSON.stringify(
                        currentMessage.meta_data != null || {},
                    )
                }
                if (response === "edit_contact_details") {
                    // Edit contact details - modify some fields before sending back
                    const originalData = (currentMessage.meta_data ??
                        {}) as Record<string, unknown>
                    const editedData: Record<string, unknown> = {
                        ...originalData,
                        modified_by_test: true,
                    }
                    if (typeof (originalData as any).email === "string") {
                        editedData.email = "test@example.com"
                    }
                    if (typeof (originalData as any).phone === "string") {
                        editedData.phone = "+46701234567"
                    }
                    return JSON.stringify(editedData)
                }
                if (response === "yes") {
                    return "yes"
                }
                if (response === "no") {
                    return "no"
                }
                if (response === "continue") {
                    return "continue"
                }
                // Custom text response
                return response
            },
            [],
        )

        // Helper function to send message and get SSE response
        const sendMessageAndGetResponse = useCallback(
            async (
                authApiUrl: string,
                conversationId: string,
                message: string,
                token: string,
                logMessage: (msg: string, isVerbose?: boolean) => void,
            ): Promise<ChatbotMessage | null> => {
                try {
                    const response = await fetch(
                        `${authApiUrl}/v1/chatbot/message-v2/${conversationId}`,
                        {
                            method: "POST",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                                Accept: "text/event-stream",
                            },
                            body: JSON.stringify({ message }),
                        },
                    )

                    if (!response.ok) {
                        const errorText = await response.text()
                        logMessage(
                            `Failed to send message: ${response.status} - ${errorText}`,
                        )
                        return null
                    }

                    logMessage(
                        `Response headers: Content-Type=${response.headers.get(
                            "content-type",
                        )}`,
                        true,
                    )

                    logMessage("Response sent successfully", true)

                    // Handle SSE response
                    if (response.body != null) {
                        const reader = response.body.getReader()
                        const decoder = new TextDecoder()
                        let baseMessage: ChatbotMessage | null = null
                        let accumulatedText = ""
                        let _isComplete = false

                        while (true) {
                            const { done, value } = await reader.read()
                            if (done) break

                            const chunk = decoder.decode(value)
                            const lines = chunk.split("\n")

                            for (const line of lines) {
                                if (line.trim()) {
                                    // Handle SSE event: lines
                                    if (line.startsWith("event: ")) {
                                        const eventType = line.slice(7).trim()
                                        if (eventType === "EOF") {
                                            logMessage(
                                                `SSE stream completed`,
                                                true,
                                            )
                                            _isComplete = true
                                        }
                                        continue
                                    }

                                    let jsonData: any = null

                                    // Try parsing as "data: " prefixed SSE
                                    if (line.startsWith("data: ")) {
                                        try {
                                            jsonData = JSON.parse(line.slice(6))
                                        } catch (e) {
                                            // Skip non-JSON data lines
                                            continue
                                        }
                                    } else {
                                        // Try parsing as raw JSON
                                        try {
                                            jsonData = JSON.parse(line)
                                        } catch (e) {
                                            logMessage(
                                                `SSE Line: ${line}`,
                                                true,
                                            )
                                            continue
                                        }
                                    }

                                    if (jsonData) {
                                        // Handle initial message with metadata
                                        if (
                                            jsonData.messages &&
                                            jsonData.messages.length > 0
                                        ) {
                                            baseMessage = jsonData.messages[0]
                                            if (baseMessage != null) {
                                                logMessage(
                                                    `Got base message: ${baseMessage.message_type}`,
                                                    true,
                                                )
                                            }
                                        }
                                        // Handle streaming content chunks
                                        else if (
                                            jsonData.event ===
                                            "ANSWER_CONTINUED"
                                        ) {
                                            accumulatedText +=
                                                jsonData.data || ""
                                            logMessage(
                                                `Accumulating text... (${accumulatedText.length} chars)`,
                                                true,
                                            )
                                        }
                                        // Handle other message types
                                        else if (jsonData.message) {
                                            baseMessage = jsonData.message
                                            if (baseMessage != null) {
                                                logMessage(
                                                    `Got complete message: ${baseMessage.message_type}`,
                                                    true,
                                                )
                                            }
                                            _isComplete = true
                                        }
                                    }
                                }
                            }
                        }

                        if (baseMessage != null) {
                            // Build final message with accumulated text
                            const finalMessage = {
                                ...baseMessage,
                                text: accumulatedText || baseMessage.text || "",
                            }

                            logMessage(
                                `Final message assembled: ${
                                    finalMessage.message_type
                                } - "${finalMessage.text.slice(0, 100)}${
                                    finalMessage.text.length > 100 ? "..." : ""
                                }"`,
                            )
                            return finalMessage
                        }
                    }

                    return null
                } catch (error) {
                    logMessage(`Error in SSE communication: ${error}`)
                    return null
                }
            },
            [],
        )

        const createExecution = useCallback(
            (testConfigurationId: number) =>
                ChatbotTestExecutionService.postV3AdminChatbotTestExecution({
                    request: {
                        test_configuration_id: testConfigurationId,
                        access_group_id: selectedAccessGroupId,
                    },
                }),
            [selectedAccessGroupId],
        )

        const updateExecution = useCallback(
            (
                executionId: number,
                data: adminv3_UpdateChatbotTestExecutionRequest,
            ) =>
                ChatbotTestExecutionService.putV3AdminChatbotTestExecution({
                    id: executionId,
                    request: data,
                }),
            [],
        )

        const runRealTestExecution = useCallback(async () => {
            if (selectedConfiguration == null) {
                setError("No configuration selected")
                return
            }

            // Validate configuration - if config doesn't have test_config values,
            // require them to be set in the UI
            const testConfig = selectedConfiguration.test_config as unknown as {
                tenant_id?: string
                subdomain?: string
                api_environment?: string
            } | null

            const hasConfigValues =
                testConfig &&
                (testConfig.tenant_id ||
                    testConfig.subdomain ||
                    testConfig.api_environment)

            if (!hasConfigValues) {
                // Require all values to be set in UI
                if (!tenantId.trim()) {
                    setError("Tenant ID is required (not set in configuration)")
                    return
                }
                if (!subdomain.trim()) {
                    setError("Subdomain is required (not set in configuration)")
                    return
                }
                if (!apiEnvironment.trim()) {
                    setError(
                        "API Environment is required (not set in configuration)",
                    )
                    return
                }
            }

            setIsRunning(true)
            setError(null)
            setTestResult(null)
            setTestLog([])

            try {
                logMessage("Creating test execution record...")
                const execution = await createExecution(
                    selectedConfiguration.id as number,
                )
                logMessage(`Test execution created with ID: ${execution.id}`)

                const config =
                    selectedConfiguration.rules_config as unknown as {
                        entry_point_config?: {
                            entry_point?: string
                            form_id?: number
                        }
                        rules?: Array<any>
                        end_conditions?: Array<any>
                        settings?: { max_turns?: number }
                    }

                // Use configurable values from UI
                const environment = apiEnvironment
                const targetSubdomain = subdomain
                const targetTenantId = tenantId

                logMessage(`Environment: ${environment}`, true)
                logMessage(`Tenant ID: ${tenantId}`, true)
                logMessage(`Subdomain: ${subdomain}`, true)

                // Authentication - use proper API URL based on environment
                logMessage("Starting authentication...")
                let authApiUrl = ""
                if (environment === "stage") {
                    authApiUrl = "https://stage-api.avy.se/api"
                } else if (
                    environment === "dev" ||
                    environment === "development"
                ) {
                    authApiUrl = "https://dev-api.avy.se/api"
                } else {
                    authApiUrl = `https://${targetSubdomain}.avy.se/api`
                }

                const authResponse = await fetch(
                    `${authApiUrl}/v1/auth/test-login`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            tenant_id: Number(targetTenantId),
                        }),
                    },
                )

                if (!authResponse.ok) {
                    throw new Error(
                        `Authentication failed: ${authResponse.status}`,
                    )
                }

                const authData = await authResponse.json()
                const token = authData.token
                logMessage("Authentication successful")

                // Get apartment information
                logMessage("Getting apartment information...", true)
                const apartmentResponse = await fetch(
                    `${authApiUrl}/v1/tenants/apartments?subdomain=${targetSubdomain}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    },
                )

                if (!apartmentResponse.ok) {
                    throw new Error(
                        `Failed to get apartment info: ${apartmentResponse.status}`,
                    )
                }

                const apartmentData = await apartmentResponse.json()
                // The apartments endpoint returns an array directly, get the first apartment
                const firstApartment = Array.isArray(apartmentData)
                    ? apartmentData[0]
                    : undefined
                const propertyObjectId = firstApartment?.property_object_id
                logMessage(`Property Object ID: ${propertyObjectId}`, true)

                if (!propertyObjectId) {
                    throw new Error(
                        "No apartment found or missing property_object_id",
                    )
                }

                // Update token with property
                logMessage("Updating token with property...", true)
                const tokenUpdateResponse = await fetch(
                    `${authApiUrl}/v1/tenants/token`,
                    {
                        method: "PATCH",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            property_object_id: propertyObjectId,
                        }),
                    },
                )

                if (!tokenUpdateResponse.ok) {
                    throw new Error(
                        `Token update failed: ${tokenUpdateResponse.status}`,
                    )
                }
                // Token is updated in place, continue using the same token
                logMessage("Token updated", true)

                // Start conversation
                const entryPoint =
                    config.entry_point_config?.entry_point || "qa_agent"
                const formId = config.entry_point_config?.form_id

                // Validate dynamic form agent configuration
                if (entryPoint === "dynamic_form_agent" && !formId) {
                    throw new Error(
                        "Form ID is required for dynamic_form_agent entry point",
                    )
                }

                logMessage(
                    `Starting conversation with entry point: ${entryPoint}`,
                )

                // Build request body based on entry point type
                const baseBody: Record<string, unknown> = {
                    entry_point: entryPoint,
                }
                const requestBody: Record<string, unknown> =
                    entryPoint === "dynamic_form_agent" && formId
                        ? { ...baseBody, entry_point_id: formId }
                        : baseBody

                logMessage(`Request Body: ${JSON.stringify(requestBody)}`, true)

                const startResponse = await fetch(
                    `${authApiUrl}/v1/chatbot/start-conversation`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(requestBody),
                    },
                )

                if (!startResponse.ok) {
                    throw new Error(
                        `Failed to start conversation: ${startResponse.status}`,
                    )
                }

                const startData = await startResponse.json()
                logMessage(
                    `Start conversation response: ${JSON.stringify(startData)}`,
                    true,
                )

                const conversationId = startData.conversation_id
                logMessage(`Conversation started with ID: ${conversationId}`)

                // Simplified test execution for now
                let turnCount = 1
                let currentMessage =
                    startData.message || startData.messages?.[0]
                let success = false
                let completionReason = "test_completed"

                // Process the initial message

                logMessage(
                    `Initial message: ${JSON.stringify(currentMessage)}`,
                    true,
                )

                // Full conversation loop with turn-based processing
                const rules = (config.rules ?? []) as Array<
                    Record<string, unknown>
                >
                const endConditions = (config.end_conditions ?? []) as Array<
                    Record<string, unknown>
                >
                logMessage(
                    `Loaded ${rules.length} rules and ${endConditions.length} end conditions`,
                    true,
                )
                logMessage(
                    `End conditions: ${JSON.stringify(endConditions, null, 2)}`,
                    true,
                )
                const maxTurns = config.settings?.max_turns || 20

                while (turnCount <= maxTurns && currentMessage) {
                    logMessage(`Turn ${turnCount}: Processing message...`)

                    // Check end conditions FIRST (before processing rules)
                    logMessage(
                        `Checking ${endConditions.length} end conditions...`,
                        true,
                    )
                    let shouldEnd = false
                    // Safe to ignore lint here; closure does not capture mutated vars in a harmful way
                    // eslint-disable-next-line no-loop-func
                    // eslint-disable-next-line no-loop-func
                    for (const endCondition of endConditions) {
                        const endResult = checkEndCondition(
                            endCondition,
                            currentMessage,
                        )
                        if (endResult.met) {
                            logMessage(
                                `End condition met: ${endResult.description}`,
                            )
                            // Set success based on the result field
                            success = endResult.result === "pass"
                            completionReason =
                                endResult.result === "pass"
                                    ? "End condition matched - test passed"
                                    : "End condition matched - test failed"
                            logMessage(
                                `Test ${
                                    endResult.result === "pass"
                                        ? "PASSED"
                                        : "FAILED"
                                }: ${endResult.description}`,
                            )
                            shouldEnd = true
                            break
                        }
                    }

                    if (shouldEnd) {
                        break
                    }

                    logMessage(
                        `Processing: ${currentMessage.message_type} - "${
                            currentMessage.text || ""
                        }"`,
                        true,
                    )

                    // Find matching rule for this message type without declaring a function inside the loop (no-loop-func)
                    let matchingRule:
                        | {
                              condition?: { message_type?: string }
                              response?: unknown
                          }
                        | undefined
                    for (let i = 0; i < (rules as Array<unknown>).length; i++) {
                        const rule = (
                            rules as Array<{
                                condition?: { message_type?: string }
                                response?: unknown
                            }>
                        )[i]
                        if (
                            rule &&
                            rule.condition != null &&
                            rule.condition.message_type ===
                                currentMessage.message_type
                        ) {
                            matchingRule = rule
                            break
                        }
                    }

                    if (matchingRule) {
                        logMessage(
                            `Found rule for ${currentMessage.message_type}`,
                            true,
                        )

                        const responseText = processRuleResponse(
                            String(matchingRule.response ?? ""),
                            currentMessage,
                        )
                        logMessage(`Rule response: "${responseText}"`, true)

                        if (responseText && responseText.trim()) {
                            logMessage(`Sending response: "${responseText}"`)

                            // Message sent to bot

                            // Send message and get response via SSE
                            const nextMessage = await sendMessageAndGetResponse(
                                authApiUrl,
                                conversationId,
                                responseText,
                                token,
                                logMessage,
                            )

                            if (nextMessage != null) {
                                // Message received from bot

                                currentMessage = nextMessage
                                turnCount++
                            } else {
                                logMessage(
                                    `No response received, ending conversation`,
                                )
                                success = false
                                completionReason =
                                    "No response received from chatbot"
                                break
                            }
                        } else {
                            logMessage(`Skipping turn (empty response)`)
                            success = false
                            completionReason = "Rule generated empty response"
                            break
                        }
                    } else {
                        logMessage(
                            `No rule found for message type: ${currentMessage.message_type}`,
                        )
                        success = false
                        completionReason = `No rule configured for message type: ${currentMessage.message_type}`
                        break
                    }
                }

                if (turnCount > maxTurns) {
                    logMessage(`Test reached maximum turns (${maxTurns})`)
                    success = false
                    completionReason =
                        "Test reached maximum turns without completing"
                } else if (!currentMessage) {
                    logMessage(`No message to process on turn ${turnCount}`)
                    success = false
                    completionReason = "No message available to process"
                }

                // Test completed

                // Update execution with results
                const duration = 20000 // Placeholder
                await updateExecution(execution.id as number, {
                    status: "completed",
                    success: success,
                    turn_count: turnCount,
                    duration_ms: duration,
                    access_group_id: selectedAccessGroupId,
                })

                if (success) {
                    logMessage("Test execution completed successfully!")
                } else {
                    logMessage(`Test execution failed: ${completionReason}`)
                }
                setTestResult({
                    success,
                    turnCount,
                    duration,
                    completionReason,
                })
            } catch (err) {
                const errorMessage =
                    err instanceof Error
                        ? err.message
                        : "Unknown error occurred"
                logMessage(`Test failed: ${errorMessage}`)
                setError(errorMessage)

                // Update execution with error
                try {
                    await updateExecution(selectedConfiguration.id as number, {
                        status: "failed",
                        success: false,
                        error_message: errorMessage,
                        access_group_id: selectedAccessGroupId,
                    })
                } catch (updateErr) {
                    // eslint-disable-next-line no-console
                    console.error(
                        "Failed to update execution with error:",
                        updateErr,
                    )
                }
            } finally {
                setIsRunning(false)
            }
        }, [
            selectedConfiguration,
            createExecution,
            updateExecution,
            logMessage,
            selectedAccessGroupId,
            apiEnvironment,
            subdomain,
            tenantId,
            checkEndCondition,
            processRuleResponse,
            sendMessageAndGetResponse,
        ])

        return (
            <Box>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Test Runner
                        </Typography>

                        {/* Access Group Selection */}
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Access Group</InputLabel>
                            <Select
                                value={selectedAccessGroupId}
                                onChange={(e) =>
                                    setSelectedAccessGroupId(
                                        e.target.value as number,
                                    )
                                }
                                label="Access Group"
                            >
                                {globalStore.session.accessGroups.map(
                                    (group) => (
                                        <MenuItem
                                            key={group.id}
                                            value={group.id}
                                        >
                                            {group.name}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                        </FormControl>

                        {/* Test Configuration */}
                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            Test Configuration{" "}
                            {selectedConfiguration?.test_config
                                ? "(Runtime Override)"
                                : "(Required)"}
                        </Typography>
                        {selectedConfiguration?.test_config && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Configuration has predefined environment
                                settings. You can override them below.
                            </Alert>
                        )}

                        <FormControl fullWidth margin="normal">
                            <InputLabel>Environment</InputLabel>
                            <Select
                                value={apiEnvironment}
                                onChange={(e) =>
                                    setApiEnvironment(e.target.value)
                                }
                                label="Environment"
                            >
                                <MenuItem value="stage">Stage</MenuItem>
                                <MenuItem value="dev">Development</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth margin="normal">
                            <TextField
                                label="Tenant ID"
                                value={tenantId}
                                onChange={(e) => setTenantId(e.target.value)}
                                placeholder="51002"
                                helperText={
                                    selectedConfiguration?.test_config
                                        ? "Override config tenant ID"
                                        : "Required: tenant ID for testing"
                                }
                            />
                        </FormControl>

                        <FormControl fullWidth margin="normal">
                            <TextField
                                label="Subdomain"
                                value={subdomain}
                                onChange={(e) => setSubdomain(e.target.value)}
                                placeholder="stage-prodteam.avy.se"
                                helperText={
                                    selectedConfiguration?.test_config
                                        ? "Override config subdomain"
                                        : "Required: subdomain for chatbot API calls"
                                }
                            />
                        </FormControl>

                        <Divider sx={{ my: 2 }} />

                        <FormControl fullWidth margin="normal">
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Log Detail Level
                                </Typography>
                                <Button
                                    size="small"
                                    variant={
                                        verboseLogging
                                            ? "contained"
                                            : "outlined"
                                    }
                                    color={
                                        verboseLogging ? "primary" : "inherit"
                                    }
                                    onClick={() =>
                                        setVerboseLogging(!verboseLogging)
                                    }
                                    sx={{ minWidth: "auto", px: 2 }}
                                >
                                    Verbose
                                </Button>
                            </Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                {verboseLogging
                                    ? "Shows detailed technical information, API calls, and debug data"
                                    : "Shows only essential test progress and results"}
                            </Typography>
                        </FormControl>

                        <Box sx={{ mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={runRealTestExecution}
                                disabled={
                                    selectedConfiguration == null || isRunning
                                }
                                startIcon={
                                    isRunning ? (
                                        <CircularProgress size={20} />
                                    ) : undefined
                                }
                            >
                                {isRunning ? "Running Test..." : "Run Test"}
                            </Button>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {testResult != null && (
                            <Alert
                                severity={
                                    testResult.success ? "success" : "error"
                                }
                                sx={{ mb: 2 }}
                            >
                                Test {testResult.success ? "passed" : "failed"}{" "}
                                -{testResult.turnCount} turns,{" "}
                                {testResult.duration}ms
                                {testResult.completionReason &&
                                    ` (${testResult.completionReason})`}
                            </Alert>
                        )}

                        {/* Test Log */}
                        {testLog.length > 0 && (
                            <Paper
                                ref={testLogRef}
                                sx={{
                                    p: 2,
                                    maxHeight: 400,
                                    overflow: "auto",
                                    backgroundColor: "#1e1e1e",
                                    color: "#fff",
                                    fontFamily: "monospace",
                                    fontSize: "0.875rem",
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    sx={{ color: "#fff" }}
                                >
                                    Live Test Log (
                                    {verboseLogging ? "Verbose" : "Simple"}{" "}
                                    mode)
                                </Typography>
                                {testLog.map((log, index) => (
                                    <div key={index}>{log}</div>
                                ))}
                            </Paper>
                        )}
                    </CardContent>
                </Card>
            </Box>
        )
    },
)

export default TestRunner
